import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import type { AdminPuzzleDto, AdminPuzzleInput } from '@chessx/shared/types'
import { PUZZLE_GOALS, PUZZLE_SIDES, PUZZLE_SOURCES } from '@chessx/shared/constants'

import { getPuzzleTitle } from '@chessx/shared/puzzle'

import PuzzlePlayer from './PuzzlePlayer'
import { PUZZLES_PATH, useDeletePuzzle, useSavePuzzle } from './PuzzlesApi'
import { PUZZLE_TEMPLATE, toPuzzleInput } from './puzzleJson'

const JSON_FIELDS = ['sourceRef', 'opponentMove', 'root', 'verdict', 'mistakeDefaults'] as const

type JsonField = (typeof JSON_FIELDS)[number]

type EditorMode = 'form' | 'json'

interface FormState {
  puzzleKey: string
  source: string
  rating: string
  themes: string
  goal: string
  mateIn: string
  sideToMove: string
  gemReward: string
  order: string
  voices: string
  isActive: boolean
  json: Record<JsonField, string>
}

const pretty = (value: unknown): string => JSON.stringify(value ?? {}, null, 2)

const optionalText = (value: number | string | undefined): string =>
  value === undefined || value === null ? '' : String(value)

const splitList = (text: string): string[] =>
  text
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)

const optionalNumber = (text: string): number | undefined =>
  text.trim() === '' ? undefined : Number(text)

const toFormState = (input: AdminPuzzleInput): FormState => ({
  puzzleKey: input.puzzleKey,
  source: input.source,
  rating: String(input.rating),
  themes: input.themes.join(', '),
  goal: input.goal,
  mateIn: optionalText(input.mateIn),
  sideToMove: input.sideToMove,
  gemReward: optionalText(input.gemReward),
  order: String(input.order),
  voices: input.voices.join(', '),
  isActive: input.isActive,
  json: Object.fromEntries(JSON_FIELDS.map(field => [field, pretty(input[field])])) as Record<
    JsonField,
    string
  >
})

const parseJsonField = (field: JsonField, text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    throw new Error(`${field} is not valid JSON`)
  }
}

const toInput = (form: FormState): AdminPuzzleInput =>
  ({
    puzzleKey: form.puzzleKey.trim(),
    source: form.source,
    rating: Number(form.rating),
    themes: splitList(form.themes),
    goal: form.goal,
    mateIn: optionalNumber(form.mateIn),
    sideToMove: form.sideToMove,
    gemReward: optionalNumber(form.gemReward),
    order: Number(form.order),
    voices: splitList(form.voices),
    isActive: form.isActive,
    ...Object.fromEntries(
      JSON_FIELDS.map(field => [field, parseJsonField(field, form.json[field])])
    )
  }) as AdminPuzzleInput

const tryInput = (form: FormState): AdminPuzzleInput | null => {
  try {
    return toInput(form)
  } catch {
    return null
  }
}

const initialInput = (puzzle: AdminPuzzleDto | null): AdminPuzzleInput =>
  puzzle ? toPuzzleInput(puzzle) : { ...PUZZLE_TEMPLATE, puzzleKey: '' }

interface PuzzleEditorProps {
  puzzle: AdminPuzzleDto | null
}

const PuzzleEditor = ({ puzzle }: PuzzleEditorProps) => {
  const navigate = useNavigate()
  const [mode, setMode] = useState<EditorMode>('form')
  const [form, setForm] = useState<FormState>(() => toFormState(initialInput(puzzle)))
  const [rawJson, setRawJson] = useState(() => pretty(initialInput(puzzle)))
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const savePuzzle = useSavePuzzle()
  const deletePuzzle = useDeletePuzzle()

  const preview = useMemo(() => {
    if (mode === 'form') return tryInput(form)
    try {
      return JSON.parse(rawJson) as AdminPuzzleInput
    } catch {
      return null
    }
  }, [mode, form, rawJson])

  const setField =
    <K extends keyof Omit<FormState, 'json'>>(key: K) =>
    (value: FormState[K]) =>
      setForm(current => ({ ...current, [key]: value }))

  const setJsonField = (field: JsonField) => (value: string) =>
    setForm(current => ({ ...current, json: { ...current.json, [field]: value } }))

  const switchMode = (next: EditorMode) => {
    if (next === mode) return
    try {
      if (next === 'json') setRawJson(pretty(toInput(form)))
      else setForm(toFormState(JSON.parse(rawJson) as AdminPuzzleInput))
      setMode(next)
      setError(null)
    } catch (switchError) {
      setError(switchError instanceof Error ? switchError.message : String(switchError))
    }
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    let input: AdminPuzzleInput
    try {
      input = mode === 'form' ? toInput(form) : (JSON.parse(rawJson) as AdminPuzzleInput)
    } catch (parseError) {
      setError(parseError instanceof Error ? parseError.message : String(parseError))
      return
    }
    setError(null)
    const saved = await savePuzzle.mutateAsync({ id: puzzle?.id, input }).catch(() => null)
    if (saved && !puzzle) navigate(`${PUZZLES_PATH}/${saved.id}`, { replace: true })
  }

  const confirmDelete = async () => {
    if (!puzzle) return
    const deleted = await deletePuzzle
      .mutateAsync(puzzle.id)
      .then(() => true)
      .catch(() => false)
    setConfirmingDelete(false)
    if (deleted) navigate(PUZZLES_PATH)
  }

  return (
    <form className="pz-page pz-edit-page" onSubmit={submit}>
      <div className="pz-header">
        <div>
          <button type="button" className="pz-back" onClick={() => navigate(PUZZLES_PATH)}>
            ← All puzzles
          </button>
          <h3 className="pz-title">
            {preview && Array.isArray(preview.themes) ? getPuzzleTitle(preview) : 'New puzzle'}
          </h3>
          <code className="pz-edit-key">{puzzle ? puzzle.puzzleKey : 'New puzzle'}</code>
          <span className="pz-subtitle">
            {[
              preview && `${preview.sideToMove} to move · rating ${preview.rating}`,
              puzzle && `last updated ${new Date(puzzle.updatedAt).toLocaleString()}`
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <div className="pz-header-actions">
          {puzzle && (
            <button
              type="button"
              className="pz-btn pz-btn-danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          )}
          <button type="submit" className="pz-btn pz-btn-primary" disabled={savePuzzle.isPending}>
            {savePuzzle.isPending ? 'Saving…' : puzzle ? 'Save changes' : 'Create puzzle'}
          </button>
        </div>
      </div>

      {error && <div className="pz-error">{error}</div>}

      <div className="pz-edit-layout">
        <section className="pz-card pz-edit-form">
          <div className="pz-tabs">
            {(['form', 'json'] as EditorMode[]).map(option => (
              <button
                key={option}
                type="button"
                className={`pz-tab ${mode === option ? 'active' : ''}`}
                onClick={() => switchMode(option)}
              >
                {option === 'form' ? 'Form' : 'Raw JSON'}
              </button>
            ))}
          </div>

          <div className="pz-editor-main">
            {mode === 'form' ? (
              <>
                <div className="pz-grid">
                  <label className="pz-field pz-span-2">
                    <span>Puzzle key</span>
                    <input
                      className="pz-input"
                      required
                      value={form.puzzleKey}
                      placeholder="lichess:abc12"
                      onChange={event => setField('puzzleKey')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field">
                    <span>Source</span>
                    <select
                      className="pz-input"
                      value={form.source}
                      onChange={event => setField('source')(event.target.value)}
                    >
                      {PUZZLE_SOURCES.map(option => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pz-field">
                    <span>Order</span>
                    <input
                      className="pz-input"
                      type="number"
                      required
                      value={form.order}
                      onChange={event => setField('order')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field">
                    <span>Rating</span>
                    <input
                      className="pz-input"
                      type="number"
                      required
                      value={form.rating}
                      onChange={event => setField('rating')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field">
                    <span>Goal</span>
                    <select
                      className="pz-input"
                      value={form.goal}
                      onChange={event => setField('goal')(event.target.value)}
                    >
                      {PUZZLE_GOALS.map(option => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pz-field">
                    <span>Mate in</span>
                    <input
                      className="pz-input"
                      type="number"
                      min={1}
                      value={form.mateIn}
                      onChange={event => setField('mateIn')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field">
                    <span>Side to move</span>
                    <select
                      className="pz-input"
                      value={form.sideToMove}
                      onChange={event => setField('sideToMove')(event.target.value)}
                    >
                      {PUZZLE_SIDES.map(option => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pz-field">
                    <span>Gem reward</span>
                    <input
                      className="pz-input"
                      type="number"
                      min={0}
                      value={form.gemReward}
                      onChange={event => setField('gemReward')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field pz-span-2">
                    <span>Themes (comma separated)</span>
                    <input
                      className="pz-input"
                      value={form.themes}
                      onChange={event => setField('themes')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field">
                    <span>Voices</span>
                    <input
                      className="pz-input"
                      value={form.voices}
                      onChange={event => setField('voices')(event.target.value)}
                    />
                  </label>
                  <label className="pz-field pz-check">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={event => setField('isActive')(event.target.checked)}
                    />
                    <span>Active</span>
                  </label>
                </div>

                {JSON_FIELDS.map(field => (
                  <label key={field} className="pz-field">
                    <span>{field} (JSON)</span>
                    <textarea
                      className="pz-code"
                      rows={field === 'root' ? 14 : 6}
                      spellCheck={false}
                      value={form.json[field]}
                      onChange={event => setJsonField(field)(event.target.value)}
                    />
                  </label>
                ))}
              </>
            ) : (
              <textarea
                className="pz-code pz-code-full"
                spellCheck={false}
                value={rawJson}
                onChange={event => setRawJson(event.target.value)}
              />
            )}
          </div>
        </section>

        <aside className="pz-card pz-edit-preview">
          <PuzzlePlayer key={JSON.stringify(preview)} puzzle={preview} />
        </aside>
      </div>

      <Modal centered show={confirmingDelete} onHide={() => setConfirmingDelete(false)}>
        <div className="pz-modal">
          <h4 className="pz-modal-title">Delete puzzle</h4>
          <p className="pz-modal-body">
            Permanently delete <code>{puzzle?.puzzleKey}</code>? Players will stop seeing it on
            their next sync.
          </p>
          <div className="pz-modal-actions">
            <button
              type="button"
              className="pz-btn pz-btn-ghost"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pz-btn pz-btn-danger"
              disabled={deletePuzzle.isPending}
              onClick={confirmDelete}
            >
              {deletePuzzle.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </form>
  )
}

export default PuzzleEditor
